/** "Today" per the daily logbook's 08:00-08:00 day (see LOGBOOK_DAY_START_HOUR in
 * supabase/functions/api/modules/readings.ts) — a reading taken at 2am belongs to yesterday's
 * log, which is still the active/default one until 8am, not today's (still-empty) one. */
export function logbookToday(): string {
  return new Date(Date.now() - 8 * 3600000).toISOString().slice(0, 10);
}

export function fmtTime(iso: string | null | undefined, tz?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: tz });
}

export function fmtDateTime(iso: string | null | undefined, tz?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', timeZone: tz,
  });
}

/** "2026-09-09" -> "09-09-2026". Passes through anything that isn't yyyy-mm-dd. */
export function fmtDMY(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : isoDate;
}

export function fmtCountdown(ms: number): string {
  if (ms <= 0) return '00:00';
  const total = Math.ceil(ms / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  // a monthly-cadence gate can leave days on the clock — HH:MM:SS stops being readable well past 24h
  if (total >= 86400) {
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    return `${d}d ${h}h`;
  }
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/** "90" -> "1h 30m"; "2880" -> "2 days"; "45" -> "45 min". Whole minutes only. */
export function fmtDuration(min: number): string {
  if (!Number.isFinite(min) || min <= 0) return '—';
  if (min % 1440 === 0) { const d = min / 1440; return `${d} day${d === 1 ? '' : 's'}`; }
  if (min >= 1440) { const d = Math.floor(min / 1440), h = Math.round((min % 1440) / 60); return h ? `${d}d ${h}h` : `${d}d`; }
  if (min % 60 === 0) { const h = min / 60; return `${h} hour${h === 1 ? '' : 's'}`; }
  if (min >= 60) { const h = Math.floor(min / 60), m = min % 60; return `${h}h ${m}m`; }
  return `${min} min`;
}
